import { Button, MenuItem } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { FocusEvent, default as React } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import KeyboardDatePickerController from '../../../components/DatePicker/KeyboardDatePickerController';
import FormContainer from '../../../components/Forms/FormContainer';
import TextInputController from '../../../components/Inputs/TextInputController/TextInputController';
import SelectController from '../../../components/Select/SelectController';
import { DATE_FORMAT_DB } from '../../../constants/date';
import { dateToDbDate, dbDateToDate } from '../../../helpers/dateHelper';
import { tFormKey, tProjectKey } from '../../../helpers/translate';
import { editInvoiceData, invoiceSetData } from '../../../redux/accounting/actions';
import { InvoiceData } from '../../../redux/accounting/definitions';
import { selectInvoice } from '../../../redux/accounting/selectors';
import { CodeNamePair } from '../../../redux/common/definitions';
import { selectPaymentTypeCodes } from '../../../redux/metadata/selectors';
import { AppState } from '../../../redux/root-reducer';
import { ModifyInvoiceSchema } from '../../../validations/formSchema';
import styles from './InvoiceModificationModal.module.scss';

interface Props {
  invoiceData: InvoiceData;
  paymentTypeCodesMetadata: CodeNamePair[];
  setData: (key: string, value: string | number) => void;
  onCloseModal: () => void;
  onSubmitOk?: () => void;
  editInvoiceData: (invoice: InvoiceData) => void;
}

interface ModifyInvoiceData extends InvoiceData {
  log_comment: string;
}

const InvoiceModificationModal = ({
  invoiceData,
  paymentTypeCodesMetadata,
  setData,
  editInvoiceData,
  onCloseModal,
  onSubmitOk,
}: Props) => {
  const {
    description,
    reference_number,
    invoice_date,
    due_date,
    contract_number,
    total_amount,
    entity_contracts,
    payment_type_code,
    account_number,
    account_numbers,
  } = invoiceData;

  const handleSelectChange = (target: any) => {
    if (target.name) {
      setData(target.name, target.value);
    }
  };

  const { handleSubmit, triggerValidation, setValue, errors, control } = useForm<ModifyInvoiceData>({
    mode: 'onSubmit',
    submitFocusError: true,
    validationSchema: ModifyInvoiceSchema,
  });

  const handleSetDate = (name: string, date: MaterialUiPickersDate) => {
    if (date) {
      const formattedDate = date.format(DATE_FORMAT_DB);
      setData(name, formattedDate);
      setValue(name, formattedDate);
    }
  };

  const onBlurSetDate = (name: string, date?: string) => {
    if (date) {
      const formattedDate = dateToDbDate(date);
      setData(name, formattedDate);
      setValue(name, formattedDate);
    }
  };

  const asignDateValue = (date: string | null) => {
    return !!date ? dbDateToDate(date) : undefined;
  };

  const onSubmit = (data: InvoiceData) => {
    const invoice = { ...invoiceData, ...data };
    editInvoiceData(invoice);
    if (onSubmitOk) {
      onSubmitOk();
    }
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormContainer title={''} className={styles.formModalContainer}>
          <div className={styles.subContainer}>
            <TextInputController
              fullWidth
              control={control}
              schema={ModifyInvoiceSchema}
              label={tFormKey('Número de factura')}
              name="reference_number"
              defaultValue={reference_number}
              errors={errors}
              onBlur={(e: FocusEvent<HTMLInputElement>) => {
                setData(e.target.name, e.target.value);
                triggerValidation('reference_number');
              }}
            />
            <KeyboardDatePickerController
              fullWidth
              label={tFormKey('Fecha factura')}
              name="invoice_date"
              control={control}
              schema={ModifyInvoiceSchema}
              defaultValue={asignDateValue(invoice_date)}
              errors={errors}
              onAccept={date => {
                handleSetDate('invoice_date', date);
              }}
              onBlur={e => {
                onBlurSetDate('invoice_date', e.target.value);
              }}
            />
            <KeyboardDatePickerController
              fullWidth
              label={tFormKey('Fecha de vencimiento')}
              name="due_date"
              control={control}
              schema={ModifyInvoiceSchema}
              defaultValue={asignDateValue(due_date)}
              errors={errors}
              onAccept={date => {
                handleSetDate('due_date', date);
              }}
              onBlur={e => {
                onBlurSetDate('due_date', e.target.value);
              }}
            />
          </div>
          <div className={styles.subContainer}>
            <SelectController
              fullWidth
              control={control}
              errors={errors}
              schema={ModifyInvoiceSchema}
              name="payment_type_code"
              defaultValue={payment_type_code}
              label={tFormKey('Tipo de efecto')}
              onClick={e => handleSelectChange(e.target)}
            >
              {paymentTypeCodesMetadata.map(type => (
                <MenuItem key={type.code} value={type.code}>
                  {type.name}
                </MenuItem>
              ))}
            </SelectController>
            <SelectController
              fullWidth
              control={control}
              errors={errors}
              schema={ModifyInvoiceSchema}
              defaultValue={account_number}
              name="account_number"
              label={tProjectKey('Número de cuenta')}
              onClick={e => handleSelectChange(e.target)}
            >
              {account_numbers?.map((iban: string, index: number) => (
                <MenuItem key={`${iban}_${index}`} value={iban}>
                  {iban}
                </MenuItem>
              ))}
            </SelectController>
            <SelectController
              fullWidth
              control={control}
              errors={errors}
              schema={ModifyInvoiceSchema}
              defaultValue={contract_number}
              name="contract_number"
              label={tFormKey('Contrato')}
              onClick={e => handleSelectChange(e.target)}
            >
              {entity_contracts?.map((v, i) => (
                <MenuItem key={`${v}_${i}`} value={v}>
                  {v}
                </MenuItem>
              ))}
            </SelectController>
          </div>

          <TextInputController
            control={control}
            schema={ModifyInvoiceSchema}
            label={tFormKey('Descripción')}
            name="description"
            defaultValue={description}
            errors={errors}
            multiline
            rows="5"
            rowsMax="5"
            fullWidth
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              setData(e.target.name, e.target.value);
              triggerValidation('description');
            }}
          />
          <TextInputController
            control={control}
            schema={ModifyInvoiceSchema}
            label={tFormKey('Comentario')}
            name="log_comment"
            errors={errors}
            multiline
            rows="5"
            rowsMax="5"
            fullWidth
          />
          <TextInputController
            type="hidden"
            control={control}
            key="total_amount"
            name="total_amount"
            errors={errors}
            schema={ModifyInvoiceSchema}
            defaultValue={total_amount}
            visible={false}
          />
        </FormContainer>
        <div className={styles.buttons}>
          <Button
            variant="outlined"
            color="primary"
            style={{ marginLeft: '10px', width: '120px' }}
            onClick={onCloseModal}
          >
            {tFormKey('Cancelar')}
          </Button>
          <Button variant="outlined" color="primary" style={{ marginLeft: '10px', width: '120px' }} type="submit">
            {tFormKey('Modificar')}
          </Button>
        </div>
      </form>
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  invoiceData: selectInvoice(state),
  paymentTypeCodesMetadata: selectPaymentTypeCodes(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  setData: (key: string, value: string | number) => dispatch(invoiceSetData(key, value)),
  editInvoiceData: (invoice: InvoiceData) => dispatch(editInvoiceData(invoice)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceModificationModal);
